import React from 'react';
import { Navigate } from 'react-router-dom';

import { getInitialUrl } from '@nxsys/constants/AppConst';
import { authRouteConfig } from './AuthRoutes';
import { errorPagesConfigs } from './ErrorPagesRoutes';
import { dashboardConfig } from './DashboardsRoutes';
import { companiesConfig } from './CompaniesRoutes';
import { employeesConfig } from './EmployeesRoutes';
import { agencyConfig } from './AgencyRoutes';
import { assignmentsConfig } from './AssignmentsRoutes';
import { hmrcUpdateConfig } from './HMRCUpdateRoutes';
import { bulkUpdateConfig } from './BulkUpdateRoutes';
import { statutoryPaymentConfig } from './StatutoryPaymentRoutes';
import { paymentsConfig } from './PaymentsRoutes';
import { deductionsConfig } from './DeductionsRoutes';
import { paymentRatesConfig } from './PaymentRatesRoutes';
import { vatCodesConfig } from './VATCodesRoutes';
import { timesheetsConfig } from './TimesheetsRoutes';
import { payslipConfig } from './PayslipRoutes';
import { earningStatementConfig } from './EarningStatementRoutes';
import { invoicesConfig } from './InvoicesRoutes';
import { payrollReportsConfig } from './PayrollReportsRoutes';
import { pensionReportsConfig } from './PensionReportsRoutes';
import { managementReportsConfig } from './ManagementReportsRoutes';
import { bankFileReportsConfig } from './BankFileReportsRoutes';
import { employeeReportsConfig } from './EmployeeReportsRoutes';
import { documentsConfig } from './DocumentsRoutes';
import { importConfig } from './ImportRoutes';
import { kidConfig } from './KIDRoutes';
import { rtiFilingsHistoryConfig } from './RTIFilingsHistoryRoutes';
import { activityLogsConfig } from './ActivityLogsRoutes';
import { permissionsConfig } from './PermissionsRoutes';
import { PayrollCountryType } from '@nxsys/types/models/Common';
import { revenueFilingsHistoryConfig } from './RevenueFilingsHistory';
import { rpnConfig } from './RPNRoutes';
import { aepnConfig } from './AEPNRoutes';
import { exportConfig } from './ExportRoutes';
import { submitPSRConfig } from './SubmitPSRRoute';
import { prospectsConfig } from './ProspectsRoutes';
import { agencyReportsConfig } from './AgencyReportsRoutes';
import { attachmentOrdersReportsConfig } from './AttachmentOrdersReportsRoutes';
import { onHoldWorkerReportsConfig } from './OnHoldWorkerReportsRoutes';
import { employeesMenuConfig } from './EmployeesMenuRoutes';
import { reportsConfig } from './ReportsRoutes';
import { accountingConfig } from './AccountingRoutes';
import { payrollOverviewConfig } from './PayrollOverviewRoutes';
import { notificationConfig } from './NotificationRoutes';
import { messageConfig } from './MessageRoutes';
import { settingsConfig } from './SettingsRoutes';
import { companyDraftConfig } from './CompanyDraftRoutes';
import { superAdminConfig } from './SuperAdminRoutes';
import routesConfig from './routeConfig';
import { changePasswordConfig } from './ChangePasswordRoutes';
import { onboardingConfig } from './OnboardingRoutes';
import { reportRoutesForCountry } from './umbrellaIrelandRoutes';

const OAuthAuthorize = React.lazy(
  () => import('../../../modules/oauth/OAuthAuthorize'),
);

const Error403 = React.lazy(
  () => import('../../../modules/errorPages/Error403'),
);

type RouteConfigGetter = (
  isBureau: boolean,
  payrollCountry: PayrollCountryType,
) => any[];

// Map sidebar route IDs to their corresponding route config modules
const routeModuleMap: Record<string, RouteConfigGetter> = {
  dashboards: () => dashboardConfig, // includes /analytics
  companies: () => companiesConfig,
  general: () => employeesMenuConfig,
  payslip: () => payslipConfig,
  'earning-statement': () => earningStatementConfig,
  payroll: () => payrollOverviewConfig,
  'employees-subcontractors': () => employeesConfig,
  prospects: () => prospectsConfig,
  'hmrc-update': (_, pc) => hmrcUpdateConfig(pc),
  'bulk-update': () => bulkUpdateConfig,
  'statutory-payments': () => statutoryPaymentConfig,
  payments: () => paymentsConfig,
  deductions: () => deductionsConfig,
  'payment-rates': () => paymentRatesConfig,
  'vat-codes': () => vatCodesConfig,
  timesheets: () => timesheetsConfig,
  invoices: () => invoicesConfig,
  reports: (ib, pc) => {
    const routes = [
      ...reportsConfig,
      ...payrollReportsConfig,
      ...pensionReportsConfig,
      ...managementReportsConfig,
      ...bankFileReportsConfig,
      ...attachmentOrdersReportsConfig,
      ...onHoldWorkerReportsConfig,
      ...employeeReportsConfig,
    ];
    // Sidebar visibility alone does not prevent direct URL access.
    const allowed = reportRoutesForCountry(routes, pc, ib);
    // Dropping a blocked path is not enough: /reports/payroll/p30, p32 and
    // cis-payment-summary would then match /reports/payroll/:payrollId and
    // open a Payment Summary Detail. Keep them registered, sent to the 404.
    const blocked = routes
      .filter((route) => !allowed.includes(route))
      .map((route) => ({
        ...route,
        element: <Navigate to="/error-pages/error-404" replace />,
      }));
    return [...allowed, ...blocked];
  },
  accounting: () => accountingConfig,
  documents: () => documentsConfig,
  import: () => importConfig,
  export: () => exportConfig,
  KID: () => kidConfig,
  'rti-filings-history': (ib, pc) => rtiFilingsHistoryConfig(ib, pc),
  'revenue-filings-history': (_, pc) => revenueFilingsHistoryConfig(pc),
  'activity-log': () => activityLogsConfig,
  permissions: (ib) => permissionsConfig(ib),
  agency: () => agencyConfig,
  assignments: () => assignmentsConfig,
  rpn: () => rpnConfig,
  aepn: () => aepnConfig,
  'submit-psr': (_, pc) => submitPSRConfig(pc),
  'agency-reports': (_, pc) => agencyReportsConfig(pc),
};

// Routes always included regardless of sidebar config
const alwaysIncludedRoutes = [
  ...notificationConfig,
  ...messageConfig,
  ...settingsConfig,
  ...superAdminConfig,
  ...changePasswordConfig,
  ...onboardingConfig,
];

// Extract all route IDs from sidebar config (including children)
const extractRouteIds = (
  sidebarConfig: (
    | { id: string; children?: { id: string }[] }
    | { type: string }
  )[],
): string[] => {
  const ids: string[] = [];
  for (const item of sidebarConfig) {
    if (!('id' in item)) continue;
    ids.push(item.id);
    if (item.children) {
      for (const child of item.children) {
        ids.push(child.id);
      }
    }
  }
  return ids;
};

const authorizedStructure = (
  isBureau: boolean,
  payrollCountry: PayrollCountryType,
  /**
   * An employee who still has onboarding to complete is confined to it: every
   * other path redirects back to /onboarding, so they cannot reach the rest of
   * the app until they are done.
   */
  lockToOnboarding = false,
) => {
  if (lockToOnboarding) {
    return {
      fallbackPath: '/onboarding',
      unAuthorizedComponent: <Error403 />,
      // Every other path is sent back to /onboarding by the anonymous
      // catch-all (see getAnonymousStructure) — a catch-all here would never
      // match, since the anonymous one is registered first and wins the tie.
      routes: [...onboardingConfig],
    };
  }

  const sidebarConfig = routesConfig(isBureau, payrollCountry);
  const routeIds = extractRouteIds(sidebarConfig);

  const routes: any[] = [];
  const addedIds = new Set<string>();

  for (const id of routeIds) {
    if (addedIds.has(id)) continue;
    addedIds.add(id);

    const getter = routeModuleMap[id];
    if (getter) {
      routes.push(...getter(isBureau, payrollCountry));
    }
  }

  return {
    fallbackPath: '/signin',
    unAuthorizedComponent: <Error403 />,
    routes: [...routes, ...alwaysIncludedRoutes],
  };
};

const getPublicStructure = () => ({
  fallbackPath: getInitialUrl(),
  routes: authRouteConfig,
});

const getAnonymousStructure = (
  /** See authorizedStructure — unknown paths go back to /onboarding. */
  lockToOnboarding = false,
) => ({
  routes: errorPagesConfigs
    .concat(companyDraftConfig)
    .concat([
      {
        path: '/oauth/authorize',
        element: <OAuthAuthorize />,
      },
      {
        path: '/',
        element: <Navigate to={getInitialUrl()} />,
      },
      {
        // The only catch-all React Router ever reaches: it is registered before
        // the authorized routes, so it wins any tie with one there. While an
        // employee is locked to onboarding, any other path (e.g. /dashboards,
        // where logging in as them lands) goes back to it instead of a 404.
        path: '*',
        element: lockToOnboarding ? (
          <Navigate to="/onboarding" replace />
        ) : (
          <Navigate to="/error-pages/error-404" />
        ),
      },
    ]),
});

export { authorizedStructure, getPublicStructure, getAnonymousStructure };
